import { postReport, postTimeEntry, putReport } from '@/api/reports'
import Btn from '@/components/forms/Btn'
import globalContext from '@/context'
import { useReports, useSubmit } from '@/hooks'
import { useContext, useRef, useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import ProjectHeader from '@/components/forms/ProjectHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { route } from 'preact-router'
import { Api, ReportType } from '@/types'
import { appendFormData, formDataToJson, isNumeric } from '@/utils'
import { FC } from 'preact/compat'

type Props = {
	type: ReportType
	submitArgs?: Partial<Parameters<typeof useSubmit>[0]>
	report?: Api.Report
	reportId?: number | string | undefined
	disabled?: boolean
}

const Report: FC<Props> = p => {
	const { t } = useTranslation()
	const [, actions] = useContext(globalContext)
	const queryClient = useQueryClient()
	const formRef = useRef<HTMLFormElement>()
	const [isComplete, setIsComplete] = useState(p.report?.form?.is_complete ?? '0')
	const { data: reports } = useReports()

	const mutation = useMutation({
		mutationKey: isNumeric(p.reportId) ? ['update-form', p.reportId] : ['create-form', p.reportId],
		mutationFn: (data: any) => {
			const formData = new FormData()
			appendFormData(formData, data);
			return isNumeric(p.reportId) ? putReport(formData) : postReport(formData)
		},
		onSuccess: (data, variables, context) => {
			actions.toastInfo(t('toasts:form_submitted'));
			setTimeout(() => {
				route('/reports');
				scrollTo({ top: 0 });
			}, 1000);
			queryClient.invalidateQueries({
				queryKey: ['forms']
			})
		},
		onError: (error, variables, context) => {
			actions.toastError(t('toasts:generic_error'))
			setTimeout(() => {
				route('/reports')
			}, 1000)
		}
	})

	const onSubmit = (event: SubmitEvent) => {
		event.preventDefault()
		const form = new FormData(event.target as HTMLFormElement)
		if (p.submitArgs?.process) {
			p.submitArgs.process(form)
		}
		mutation.mutate(formDataToJson(form))
	}

	const handleCompleteClick = (e: MouseEvent) => {
		e.preventDefault()
		const isConfirmed = window.confirm(t('driller:confirm_complete'))

		if (isConfirmed) {
			setIsComplete('1')
			setTimeout(() => {
				formRef.current?.requestSubmit()
			}, 100)
		}
	}

	return (
		<>
			<h1>{t(`routes:${p.type}`)}</h1>
			<form onSubmit={onSubmit} ref={formRef}>
				<fieldset class='dummy' disabled={p.disabled}>
					{(!p.disabled && !p.report && p.type != 'driller_prod') && (
						<Btn
							class='justify-self-end'
							disabled={!reports?.filter(r => r.form_type === p.type).length}
						>
							<a href={`/reports/filled/${p.type}`}>{t('forms:copy_from_prev')}</a>
						</Btn>
					)}
					<input hidden name='form_type' defaultValue={p.type} />
					<input hidden name="id" defaultValue={p.reportId} />
					{p.type !== 'driller_prod' ? <ProjectHeader report={p.report} type={p.type} /> : ''}
					{p.children}
					{!p.disabled && (
						<Btn submit disabled={mutation.isPending}>
							{p.type == 'driller_prod' ? t('forms:save_temporarily') : t('forms:submit')}
						</Btn>
					)}
					{
						p.type == 'driller_prod' ? (
							<input type="hidden" id="isComplete" name="form[is_complete]" value={isComplete} />
						) : ''
					}

				</fieldset>
			</form>
			{
				(p.type == 'driller_prod' && !p.disabled) ? (
					<>
						<Btn disabled={mutation.isPending} onClick={handleCompleteClick} data-complete={true} className={'button flex items-center justify-center gap-x-4 primary mx-auto my-2 px-8 py-3 bg-teal-500 text-white shadow-md disabled:bg-gray-400 disabled:shadow-none'}>
							{t('forms:complete')}
						</Btn>
					</>

				) : ''
			}
		</>
	)
}
export default Report
